import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { LoadingButton } from '@mui/lab';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import LinearProgress from '@mui/material/LinearProgress';

import { getPlatformDisplayName } from 'src/utils/platform-label';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';

import MatchRow from './match-row';
import OtherListingsPicker from './other-listings-picker';
import { liveCount, recapLine, rowsThatFit, reviewTotals, overspendAdvice } from './review-counts';
import { plural, itemKey, matchKey, DECISION_LABEL, MARKETPLACE_IMPORT } from './decisions';

// Matches the row's own height in match-row.jsx — two 48px pictures plus its padding.
const ROW_HEIGHT = 72;

// About five rows before the list starts scrolling. Short enough that the explainer above and the
// buttons below are both on screen at the same time, on any laptop.
const LIST_MAX_HEIGHT = 360;

// A plan limit puts a second list on the same screen, and two five-row lists do not fit one
// laptop — the second would sit under the fold, where a seller scrolling the first list with
// their pointer over it never finds it. The look-alikes give up two rows so both are in view.
const LIST_MAX_HEIGHT_SHARED = 216;

/**
 * The one screen an import stops on: the items it found that look like listings the seller already
 * has, and what to do about each of them.
 *
 * Everything an import finds that matches nothing is already in the seller's inventory by the time
 * this opens — it was never a question, so it was never worth asking about. That is what keeps this
 * screen to one job: for each look-alike, is it the same item or not? The default answer is the one
 * that cannot leave the seller with two of the same listing, so a seller who reads the heading and
 * presses the button gets the right outcome without deciding anything.
 *
 * The only other thing that can appear here is a plan limit: when the rest of the catalogue will not
 * all fit, somebody has to choose which parts of it come in, and it has to be the seller.
 */
export default function ImportReviewDialog ({
  open,
  onClose,
  onConfirm,
  loading,
  matches = [],
  otherListings = [],
  platform,
  importedCount = 0,
  missedCount = 0,
  remainingSlots = Infinity,
  review = MARKETPLACE_IMPORT,
}) {
  const { choices, defaultChoice, heading, explainer } = review;

  // Only what the seller has actually changed. Everything else is read as the default, so there is
  // no moment where the dialog has to be told what it already knows — and no way for a re-render to
  // wipe a review halfway through.
  const [changed, setChanged] = useState({});
  const [picked, setPicked] = useState(() => new Set());

  const marketplaceName = getPlatformDisplayName(platform);

  // A second import of the same account finds the same look-alikes under the same names, so
  // anything left behind here would answer its questions on the seller's behalf.
  useEffect(() => {
    if (open) return;
    setChanged({});
    setPicked(new Set());
  }, [open]);

  const rows = useMemo(
    () =>
      matches.map((match, index) => ({
        key: matchKey(match, platform, index),
        match,
        // A look-alike that is not live on the marketplace comes in as a draft, which the plan
        // does not charge for.
        isInactive: Boolean(match?.newListing?.isInactive),
      })),
    [matches, platform]
  );

  const others = useMemo(
    () => otherListings.map((listing, index) => ({ key: itemKey(listing, platform, index), listing })),
    [otherListings, platform]
  );

  // Whoever sent these listings could not bring them all in, so the seller has to say which ones
  // they want. When they all fit there is nothing to choose and they all come in.
  const mustChoose = liveCount(otherListings) > remainingSlots;

  const pickedOthers = useMemo(
    () => (mustChoose ? others.filter(({ key }) => picked.has(key)) : others),
    [mustChoose, others, picked]
  );

  const totals = reviewTotals({
    items: rows,
    decisions: changed,
    defaultChoice,
    pickedOthers: pickedOthers.map(({ listing }) => listing),
    remainingSlots,
  });

  // What "Select" would take: as many as fit once the seller's own new-item decisions are paid
  // for, ignoring whatever they have already picked.
  const fitting = rowsThatFit(others, remainingSlots - totals.newItemsLive);

  const decisionOf = (key) => changed[key] ?? defaultChoice;

  // Pressing one button instead of eighty. "Bring them all in as new items" is deliberately not on
  // offer: one press could put a whole catalogue over the seller's plan.
  const bulkChoices = choices.filter((choice) => DECISION_LABEL[choice]?.all);

  const setAll = (decision) => {
    setChanged(Object.fromEntries(rows.map((row) => [row.key, decision])));
  };

  const allAre = (decision) => rows.length > 0 && rows.every((row) => decisionOf(row.key) === decision);

  const togglePicked = useCallback((key) => {
    setPicked((current) => {
      const next = new Set(current);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  // A review interrupts an import to ask the seller something, so how often it is shown and how
  // often it is answered are tracked apart.
  useEffect(() => {
    if (!open) return;
    trackEvent(ANALYTICS_EVENTS.LISTING_DUPLICATE_CHECK_SHOWN, {
      platform,
      duplicateCount: matches.length,
      noDuplicateCount: otherListings.length,
    });
  }, [open, platform, matches.length, otherListings.length]);

  const handleClose = () => {
    trackEvent(ANALYTICS_EVENTS.LISTING_DUPLICATE_CHECK_DISMISSED, {
      platform,
      duplicateCount: matches.length,
      decided: Object.keys(changed).length,
    });
    onClose();
  };

  const handleConfirm = () => {
    const decisions = {};
    rows.forEach(({ key, match }) => {
      decisions[key] = {
        decision: decisionOf(key),
        newListing: match.newListing,
        existingListing: match.existingListing,
      };
    });

    trackEvent(ANALYTICS_EVENTS.LISTING_DUPLICATE_CHECK_RESOLVED, {
      platform,
      duplicateCount: matches.length,
      merged: totals.link,
      separate: totals.newItem,
      skipped: totals.leaveOut,
      notDuplicateSelected: pickedOthers.length,
    });

    onConfirm(decisions, pickedOthers.map(({ listing }) => listing));
  };

  const arrivals = importedCount > 0
    ? `We've already brought in the other ${plural(importedCount, 'item')}. Closing this won't remove them.`
    : 'Nothing has been brought in yet.';

  // An import can create fewer listings than it set out to. Staying quiet about that is how a
  // seller ends up believing they have listings in Voolist that were never brought in.
  const shortfall = missedCount > 0
    ? ` ${plural(missedCount, 'item')} didn't come through. Importing again will pick ${missedCount === 1 ? 'it' : 'them'} up.`
    : '';

  const recap = recapLine(totals);
  const overspendFix = overspendAdvice(totals);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={loading ? undefined : handleClose}>
      <DialogTitle sx={{ pb: 1 }}>{heading(matches.length)}</DialogTitle>

      <DialogContent sx={{ pb: 1 }}>
        <Stack spacing={2}>
          {loading ? <LinearProgress /> : null}

          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 520 }}>
              {explainer(marketplaceName)}
            </Typography>

            {rows.length > 1 ? (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                {bulkChoices
                  .filter((choice) => !allAre(choice))
                  .map((choice) => (
                    <Button
                      key={choice}
                      size="small"
                      color="inherit"
                      variant="outlined"
                      onClick={() => setAll(choice)}
                    >
                      {DECISION_LABEL[choice].all}
                    </Button>
                  ))}
              </Stack>
            ) : null}
          </Stack>

          <Card variant="outlined" sx={{ overflow: 'hidden' }}>
            <FixedSizeList
              height={Math.min(
                mustChoose ? LIST_MAX_HEIGHT_SHARED : LIST_MAX_HEIGHT,
                rows.length * ROW_HEIGHT
              )}
              itemCount={rows.length}
              itemSize={ROW_HEIGHT}
              width="100%"
              overscanCount={4}
            >
              {({ index, style }) => {
                const { key, match } = rows[index];

                return (
                  <div style={style}>
                    <MatchRow
                      match={match}
                      marketplaceName={marketplaceName}
                      choices={choices}
                      defaultChoice={defaultChoice}
                      decision={decisionOf(key)}
                      onChange={(decision) =>
                        setChanged((current) => ({ ...current, [key]: decision }))
                      }
                      divided={index > 0}
                    />
                  </div>
                );
              }}
            </FixedSizeList>
          </Card>

          <Typography variant="caption" color="text.secondary">
            {arrivals}
            {shortfall}
          </Typography>

          {mustChoose ? (
            <>
              <Divider />

              <OtherListingsPicker
                rows={others}
                picked={picked}
                onToggle={togglePicked}
                onSelectAsManyAsFit={() => setPicked(new Set(fitting.map((row) => row.key)))}
                onClear={() => setPicked(new Set())}
                fitCount={fitting.length}
                roomLeft={totals.roomLeft}
                atCapacity={totals.roomLeft === 0}
              />
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {recap ? (
            <Typography variant="body2" color="text.secondary">
              {recap}
            </Typography>
          ) : null}

          {overspendFix ? (
            <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
              {overspendFix}
            </Typography>
          ) : null}
        </Box>

        <Button color="inherit" onClick={handleClose} disabled={loading}>
          Not now
        </Button>

        <LoadingButton
          variant="contained"
          onClick={handleConfirm}
          loading={loading}
          disabled={totals.overBy > 0}
        >
          Finish import
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

ImportReviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  /** Called with the decisions keyed by item, and the other listings that should come in. */
  onConfirm: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  /** One entry per item that looks like something the seller already has. */
  matches: PropTypes.array,
  /** The rest of the import, when it was not brought in already. */
  otherListings: PropTypes.array,
  // Absent while the dialog sits closed on a page that has not started an import yet.
  platform: PropTypes.string,
  /** How much of the import already arrived without needing a decision. */
  importedCount: PropTypes.number,
  /** How much of it set out to arrive and did not — nothing here can be retried from this screen. */
  missedCount: PropTypes.number,
  remainingSlots: PropTypes.number,
  /** Which kind of import this is — see MARKETPLACE_IMPORT and FILE_IMPORT. */
  review: PropTypes.shape({
    choices: PropTypes.array.isRequired,
    defaultChoice: PropTypes.string.isRequired,
    heading: PropTypes.func.isRequired,
    explainer: PropTypes.func.isRequired,
  }),
};
